import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LuArrowLeft as FiArrowLeft, LuPhone as FiPhone, LuPhoneOff as FiPhoneOff, LuMic as FiMic, LuMicOff as FiMicOff } from "react-icons/lu";
import { Howl } from 'howler';

/**
 * Full-screen incoming call popup (WhatsApp-like)
 * Props:
 *  - user
 *  - onAccept(isMuted: boolean)
 *  - onDecline()
 *  - onClose()
 *  - callType: "audio" | "video"
 *  - isVideoEnabled (unused visual hint)
 */
const IncommingCallPopup = ({
    user,
    onAccept,
    onDecline,
    onClose,
    callType = "audio",
    isVideoEnabled = false,
    isAcceptingCall,
}) => {
    const callerName =
        user?.name || user?.username || "Unknown Caller";
    const callerAvatar =
        user?.profilePic ||
        user?.photoURL ||
        'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef';

    const [muted, setMuted] = useState(false);
    const [ring, setRing] = useState(true);
    const [ringtone, setRingtone] = useState(null);

    // 🔔 Play ringtone automatically when component mounts
    useEffect(() => {
        const sound = new Howl({
            src: ['/audio/ringtone-1.mp3'],
            loop: true,
            volume: 0.7,
            preload: true,
            onplayerror: function () {
                // If autoplay fails, wait for user interaction
                sound.once('unlock', function () {
                    sound.play();
                });
            }
        });

        setRingtone(sound);

        // Try to play immediately
        sound.play();

        return () => {
            if (sound) {
                sound.stop();
                sound.unload();
            }
        };
    }, []);

    // 🔔 Animate border ring effect
    useEffect(() => {
        const id = setInterval(() => setRing((r) => !r), 1000);
        return () => clearInterval(id);
    }, []);

    const stopAudio = () => {
        if (ringtone) {
            ringtone.stop();
        }
    };


    const handleAccept = () => {
        stopAudio();
        onAccept?.(muted);
    };

    const handleDecline = () => {
        stopAudio();
        onDecline?.();
    };

    const getType = () => (callType === "video" || isVideoEnabled ? "Video" : "Voice");

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[2000] flex flex-col"
            >

                <div
                    className="absolute inset-0 bg-center bg-cover"
                    style={{ backgroundImage: `url(${callerAvatar})` }}
                />
                <div className="absolute inset-0 bg-black/70 backdrop-blur-lg" />

                {/* Back */}
                {/* <button
                    onClick={onClose || onDecline}
                    className="absolute top-4 left-4 p-2 rounded-full hover:bg-white/10 focus:outline-none text-white z-50"
                    aria-label="Back"
                >
                    <FiArrowLeft className="w-6 h-6" />
                </button> */}

                {/* Mute toggle */}
                <button
                    onClick={() => setMuted((m) => !m)}
                    className="absolute top-4 right-4 p-3 rounded-full bg-black/30 hover:bg-black/40 focus:outline-none text-white z-50 transition-all flex items-center justify-center"
                    aria-label={muted ? "Unmute" : "Mute"}
                >
                    {muted ? <FiMicOff className="w-5 h-5" /> : <FiMic className="w-5 h-5" />}
                </button>

                {/* Center content */}
                <div className="flex-1 flex items-center justify-center px-4 h-[65vh] md:h-[70vh]">
                    <div className="w-full z-50 max-w-3xl h-[65vh] md:h-[70vh] rounded-sm flex flex-col items-center justify-center select-none">
                        <div className="justify-items-center px-6 text-center">
                            {/* Avatar + ping ring */}
                            <div className="relative mb-6">
                                <img
                                    src={callerAvatar}
                                    alt={callerName}
                                    className={`w-28 h-28 md:w-36 md:h-36 rounded-full object-cover mx-auto border-4 ${ring ? "border-green-500/50" : "border-transparent"
                                        } transition-all duration-1000`}
                                />
                                {ring && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="animate-ping absolute inline-flex h-40 w-40 rounded-full bg-green-400 opacity-20"></div>
                                    </div>
                                )}
                            </div>

                            <h2 className="text-white text-xl md:text-2xl font-semibold tracking-wide mb-2 line-clamp-1">
                                {callerName}
                            </h2>
                            <p className="text-white/70 text-sm uppercase tracking-wider mb-1">
                                {getType()} Call
                            </p>
                            <p className="text-white/60 text-xs uppercase tracking-widest">
                                Incoming Call
                            </p>
                        </div>
                    </div>
                </div>

                {/* Accept / Decline */}
                <div className="w-full flex items-end justify-between px-10 pb-10 z-50">
                    <div className="flex flex-col items-center space-y-2">
                        <button
                            onClick={handleDecline}
                            className="md:w-20 md:h-20 w-16 h-16 rounded-full bg-[#E35E4F] hover:bg-[#d95142] active:scale-95 transition flex items-center justify-center shadow-lg shadow-black/30"
                            aria-label="Decline call"
                        >
                            <FiPhoneOff className="md:w-8 md:h-8 w-6 h-6 text-white" />
                        </button>
                        <span className="text-white text-sm font-medium">Decline</span>
                    </div>

                    <div className="flex flex-col items-center space-y-2">
                        <button
                            onClick={handleAccept}
                            className="md:w-20 md:h-20 w-16 h-16 rounded-full bg-[#77CF77] hover:bg-[#69c169] active:scale-95 transition flex items-center justify-center shadow-lg shadow-black/30"
                            aria-label="Accept call"
                        >
                            {isAcceptingCall ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                <FiPhone className="md:w-8 md:h-8 w-6 h-6 text-white" />
                            )}
                        </button>
                        <span className="text-white text-sm font-medium">Accept</span>
                    </div>
                </div>

                {/* Mute indicator */}
                {muted && (
                    <div className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm flex items-center space-x-2">
                        <FiMicOff className="w-4 h-4" />
                        <span>Muted</span>
                    </div>
                )}
            </motion.div>
        </AnimatePresence>
    );
};

export default IncommingCallPopup;
